import { useRequest } from 'features/request/useRequest';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import type { ProjectMethodologyEnum } from 'gql/graphql';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { otherCostTypeCreateGql, otherCostTypeEditGql } from '../CatalogOthersTypeQueries';

type TCreateTypeProps = {
    onSuccess: () => void;
};

export const useCreateOrEditType = ({ onSuccess }: TCreateTypeProps) => {
    const dispatch = useDispatch();

    const memoizedRequestIds = useMemo(() => ['createCatalogType', 'updateCatalogType'], []);

    const [resetCreateData] = useResetMultipleStoreData({ requestIds: memoizedRequestIds });

    const onSuccessHandler = useCallback(() => {
        resetCreateData();
        onSuccess();
    }, [onSuccess, resetCreateData]);

    const [, , , createRequest] = useRequest({
        requestId: 'createCatalogType',
        query: otherCostTypeCreateGql,
        withEffect: false,
        dependenciesUpdater: onSuccessHandler,
    });
    const [, , , updateRequest] = useRequest({
        requestId: 'updateCatalogType',
        query: otherCostTypeEditGql,
        withEffect: false,
        dependenciesUpdater: onSuccessHandler,
    });

    const handleSubmit = useCallback(
        ({ name, methodology, id }: { id?: number; name: string; methodology: ProjectMethodologyEnum }) => {
            if (id) {
                dispatch(updateRequest({ input: { id, name, methodology } }));
            } else {
                dispatch(createRequest({ input: { name, methodology } }));
            }
        },
        [createRequest, dispatch, updateRequest],
    );

    return useMemo(() => handleSubmit, [handleSubmit]);
};
